import { type ColumnType } from 'antd/lib/table/interface';
import { observer } from 'mobx-react';

import { searchFilterFields } from 'components/UI/Table/Column';
import type DocumentWithMembershipStore from 'stores/DocumentWithMembershipStore';
import { type UserSchemaPublic } from 'stores/User';
import { DOCUMENT_ROLE_LABEL_NONE, DOCUMENT_ROLE_LABELS } from 'types/Roles';
import { makeStringFilter, makeStringSorter } from 'utils/TableUtils';

import Table from '../../Table/Table';
import UserThumbnail from '../../UserThumbnail/UserThumbnail';
import AddMemberButton from '../AddMember/AddMemberButton';
import MemberActionButtons from '../MemberActionButtons/MemberActionButtons';

function getTableColumns(
  parentDocument: DocumentWithMembershipStore
): Array<ColumnType<UserSchemaPublic>> {
  return [
    {
      title: '',
      render: (_value, record) => (
        <UserThumbnail src={record.cachedPhotoURL || record.photoURL} />
      ),
      width: 32,
    },

    {
      title: 'Name',
      defaultSortOrder: 'ascend',
      render: (_value, record) => record.displayName,
      sorter: makeStringSorter((r) => r.displayName),
      ...searchFilterFields,
      onFilter: makeStringFilter((r) => r.displayName),
    },

    {
      title: 'Email',
      render: (_value, record) => record.email,
      sorter: makeStringSorter((r) => r.email),
    },

    {
      title: 'Role',
      render: (_value, record) => {
        const binding = parentDocument.members.getById(record.uid);
        return binding
          ? DOCUMENT_ROLE_LABELS[binding.role]
          : DOCUMENT_ROLE_LABEL_NONE;
      },
    },

    {
      title: '',
      render: (_value, record) => (
        <MemberActionButtons
          parentDocument={parentDocument}
          memberId={record.uid}
        />
      ),
      width: 32,
    },
  ];
}

export interface MembersTableProps {
  parentDocument: DocumentWithMembershipStore;
  title?: string;
}

function MembersTable({ parentDocument, title }: MembersTableProps) {
  const columns = getTableColumns(parentDocument);
  const members = parentDocument.members.users.map((item) => ({
    ...item,
    key: item.uid,
  }));

  return (
    <Table
      columnsDefinition={columns}
      data={members}
      headerButtons={
        <AddMemberButton parentDocument={parentDocument} key="AddMember" />
      }
      size="small"
      title={title ?? 'Sharing'}
      variant="flat"
    />
  );
}

export default observer(MembersTable);
